import {
  Checkbox,
  FormControlLabel,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import React, { useEffect, useMemo, useState } from "react";
import { FlexContainer } from "../../../shared/components/styled/Container.styled";

/** The stable identifier stored in client config for each option (schemaId when available, falls back to label). */
export const getOptionKey = (option) =>
  typeof option === "string" ? option : (option?.schemaId || option?.label || "");

export const getOptionDisplayText = (option) =>
  typeof option === "string" ? option : option?.displayText ?? option?.label ?? "";

const optionMatchesSearch = (option, query) => {
  if (typeof option === "string") {
    return option.toLowerCase().includes(query);
  }

  return (
    option.displayText?.toLowerCase().includes(query) ||
    option.label?.toLowerCase().includes(query) ||
    option.categoryLabel?.toLowerCase().includes(query)
  );
};

const DocumentDefinitionsColumn = ({
  columnTitle = "",
  options = [],
  selection = [],
  onSelectionChange = () => {},
  loading = false,
}) => {
  const [searchText, setSearchText] = useState("");
  const [showSelectedOnly, setShowSelectedOnly] = useState(false);

  // When nothing is selected the "Show selected only" filter is disabled, so it
  // must not stay active (otherwise the list empties out and becomes unusable).
  const canShowSelectedOnly = selection.length > 0;
  const effectiveShowSelectedOnly = showSelectedOnly && canShowSelectedOnly;

  // Reset the filter once the selection empties so re-selecting an item later
  // starts in "show all" mode instead of silently re-enabling the filter.
  useEffect(() => {
    if (selection.length === 0 && showSelectedOnly) {
      setShowSelectedOnly(false);
    }
  }, [selection.length, showSelectedOnly]);

  const filteredOptions = useMemo(() => {
    let visibleOptions = options;

    if (effectiveShowSelectedOnly) {
      visibleOptions = visibleOptions.filter((option) =>
        selection.includes(getOptionKey(option)),
      );
    }

    const query = searchText.trim().toLowerCase();
    if (!query) {
      return visibleOptions;
    }

    return visibleOptions.filter((option) => optionMatchesSearch(option, query));
  }, [options, searchText, selection, effectiveShowSelectedOnly]);

  const toggleOption = (option) => {
    const key = getOptionKey(option);
    const selectedIndex = selection.indexOf(key);
    if (selectedIndex === -1) {
      onSelectionChange([...selection, key]);
      return;
    }
    onSelectionChange(selection.filter((item) => item !== key));
  };

  return (
    <FlexContainer
      flexdirection="column"
      gap="8px"
      alignitems="stretch"
      justifycontent="flex-start"
      sx={{
        flex: "1 1 280px",
        minWidth: "260px",
        maxWidth: "100%",
      }}
    >
      <Typography fontWeight="bold" fontSize={14} color="inherit">
        {columnTitle}
      </Typography>
      <TextField
        variant="standard"
        placeholder="Search document types"
        value={searchText}
        onChange={(event) => setSearchText(event.target.value)}
        disabled={loading && options.length === 0}
        fullWidth
        inputProps={{ "aria-label": `Search ${columnTitle}` }}
        InputProps={{
          endAdornment: searchText ? (
            <InputAdornment position="end">
              <IconButton
                aria-label="Clear search"
                size="small"
                edge="end"
                onClick={() => setSearchText("")}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : null,
        }}
      />
      <FlexContainer
        flexdirection="column"
        width="100%"
        alignitems="flex-start"
        justifycontent="flex-start"
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: "4px",
          backgroundColor: "background.paper",
          maxHeight: "320px",
          overflowY: "auto",
          padding: "8px 0 4px 0",
        }}
      >
        {loading && options.length === 0 ? (
          <Typography padding="12px" fontSize={14}>
            Loading document types…
          </Typography>
        ) : null}
        {!loading && filteredOptions.length === 0 ? (
          <Typography padding="12px" fontSize={14}>
            {effectiveShowSelectedOnly
              ? "No document types selected."
              : "No document types found."}
          </Typography>
        ) : null}
        {filteredOptions.map((option, index) => {
          const key = getOptionKey(option);
          const displayText = getOptionDisplayText(option);
          const optionKey =
            typeof option === "string"
              ? option
              : option?.schemaId ?? `${key}-${index}`;

          return (
            <FormControlLabel
              key={optionKey}
              control={
                <Checkbox
                  size="small"
                  checked={selection.includes(key)}
                  onChange={() => toggleOption(option)}
                />
              }
              label={
                <Typography
                  fontSize={14}
                  fontWeight={selection.includes(key) ? 600 : 400}
                >
                  {displayText}
                </Typography>
              }
              sx={{
                margin: 0,
                padding: "2px 8px",
                width: "100%",
                alignItems: "flex-start",
              }}
            />
          );
        })}
      </FlexContainer>
      <FormControlLabel
        control={
          <Checkbox
            size="small"
            checked={effectiveShowSelectedOnly}
            onChange={(event) => setShowSelectedOnly(event.target.checked)}
            disabled={loading || !canShowSelectedOnly}
          />
        }
        label={
          <Typography
            fontSize={13}
            fontWeight={effectiveShowSelectedOnly ? 600 : 400}
          >
            Show selected only ({selection.length})
          </Typography>
        }
        sx={{
          margin: 0,
          padding: "4px 0 0 0",
          alignSelf: "flex-start",
        }}
      />
    </FlexContainer>
  );
};

export default DocumentDefinitionsColumn;
